import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import useTitle from "utils/custom-hooks/useTitle";
import theme from "theme/default";
import {
  borderRadius,
  colors,
  fontWeight,
  fontSize,
  gaps,
  gradients,
  padding,
} from "theme/defaultStyle";
import GradientIcon from "theme/icons/GradientIcon";
import PolicyManagementIcon from "theme/icons/PolicyManagementIcon";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import TabNavigator from "controls/global/tab-navigator/TabNavigator";
import ActionButton from "controls/global/action-button/ActionButton";
import StewartAutocompleteWithCheckboxes, {
  OptionRenderType,
} from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { TabNavigatorItem } from "entities/UIModel";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import QualityReviewTab from "./quality-review/QualityReviewTab";
import PreCloseTab from "./pre-close/PreCloseTab";
import CloseQueueTab from "./close-queue/CloseQueueTab";
import HistoryTab from "./history/HistoryTab";
import DeletedRecordsTab from "./deleted-records/DeletedRecordsTab";
import { DUMMY_DELETED_RECORDS } from "./deleted-records/dummyDeletedRecords";
import { usePolicyManagementCounts } from "./counts/usePolicyManagementCounts";
import { PolicyManagementCounts } from "./counts/types";
import { usePolicyTransactionList } from "./usePolicyTransactionList";
import { usePolicyHistoryList } from "./usePolicyHistoryList";
import { RestorePolicyTransactionResultDto } from "utils/api/policyTransactionApi";
import { UploadRegistersModal } from "./modals";
import GlobalSearchBar from "./global-search/GlobalSearchBar";
import GlobalSearchFilterBanner from "./global-search/GlobalSearchFilterBanner";
import {
  GlobalSearchQueueKey,
  GlobalSearchQueueRow,
  GlobalSearchSeed,
  toHeaderFilterDefaults,
} from "./global-search/globalSearchConfig";
import { useStateList } from "utils/custom-hooks/useStateList";
import { useDebounced } from "utils/custom-hooks/useDebounced";
import { usePersistedRowsPerPage } from "utils/custom-hooks/usePersistedRowsPerPage";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { StageType } from "utils/data/enum";

const Container = styled("div")({
  backgroundColor: theme.palette.background.default,
  borderBottomLeftRadius: borderRadius.xlarge,
  borderBottomRightRadius: borderRadius.xlarge,
  position: "relative",
  "& .header": {
    display: "flex",
    alignItems: "center",
  },
  "& .headerContainer": {
    display: "flex",
    alignItems: "center",
    minHeight: "40px",
    margin: "40px 0px 20px 0px",
    justifyContent: "space-between",
    gap: gaps.small1,
    flexWrap: "wrap",
  },
  "& .titleQuickReferenceCard": {
    background: gradients.redGradient03,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: fontWeight.bold2,
    fontSize: fontSize.xlarge2,
    paddingLeft: padding.small1,
  },
});

const ScopeChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  padding: `${padding.xsmall1} ${padding.medium1}`,
  borderRadius: borderRadius.xlarge1,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.white,
  color: colors.grey11,
  whiteSpace: "nowrap",
  "&.active": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
});

type PolicyManagementHomeProps = {
  saveFileRef: any;
  saveFileChangeRef: any;
  tabSaveChangesRef?: any;
};

type PageScope = "all" | "national" | "nonNational";

// Page-level scope (All / National / Non-National) + State chip pushed through
// as search-API criteria. `serializeFilters` drops `undefined` and empty arrays,
// so `all` / no states naturally yield no payload entries.
function buildPageCriteria(
  scope: PageScope,
  stateVals: string[]
): Record<string, unknown> {
  const agencyType =
    scope === "national"
      ? "National"
      : scope === "nonNational"
        ? "Non-National"
        : undefined;
  return {
    AgencyType: agencyType,
    // Must match PolicyBaseCriteria.StateAbbrList — `stateVals` are state
    // abbreviations, and an unknown key would be dropped at model binding.
    StateAbbrList: stateVals,
  };
}

type StageLoaderProps = {
  scope: PageScope;
  stateVals: string[];
  counts: PolicyManagementCounts;
  refetchCounts: () => void;
  userTimeZone: string;
  // Set when the queue was opened from Global Search. Merged into the page-level
  // criteria, so column-header filters still layer on top and Reset keeps it;
  // only the banner's Clear filter removes it (which also remounts the loader).
  globalSeed?: GlobalSearchSeed | null;
};

// Column-header search inputs feed this map keyed by PolicyBaseCriteria field
// name. Text columns produce string values, multi-select columns produce
// string[] (joined to a comma-delimited string before hitting the API), and
// single-select columns bound to a backend int produce 1/0 (sent unquoted).
type HeaderFilters = Record<string, string | string[] | number | null>;

// Per-tab opt-in for the auto initial-load. When `true`, on tab activation the
// loader fires one fetch with no applied filters. When `false`, no fetch
// happens until the user clicks Apply Filters (or selects a real chip).
const ALLOW_INITIAL_LOAD_QR = true;
const ALLOW_INITIAL_LOAD_PCQ = true;
const ALLOW_INITIAL_LOAD_CQ = false;
const ALLOW_INITIAL_LOAD_HISTORY = false;

// Per-stage loaders. TabNavigator only mounts the active tab's child, so each
// loader's usePolicyTransactionList(...) hook only fires when its tab becomes
// active — exactly the "fetch on tab activation" behavior we want.
const QualityReviewTabLoader = ({ scope, stateVals, counts, refetchCounts, userTimeZone, globalSeed }: StageLoaderProps) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("receivedDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [pendingAdvancedCriteria, setPendingAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [appliedAdvancedCriteria, setAppliedAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [chipCriteria, setChipCriteria] = useState<Record<string, unknown>>({});
  // Seeded rather than set from a mount effect: an effect would flip `enabled`
  // one render after the criteria were already debounced, costing an extra fetch.
  const [hasInitiatedSearch, setHasInitiatedSearch] =
    useState<boolean>(ALLOW_INITIAL_LOAD_QR || !!globalSeed);
  const pageCriteria = useMemo(
    () => ({ ...buildPageCriteria(scope, stateVals), ...globalSeed?.criteria }),
    [scope, stateVals, globalSeed]
  );
  const headerFilterDefaults = useMemo(
    () => toHeaderFilterDefaults(globalSeed?.criteria),
    [globalSeed]
  );
  const combined = useMemo(
    () => ({
      ...pageCriteria,
      ...appliedHeaderFilters,
      ...appliedAdvancedCriteria,
      ...chipCriteria,
    }),
    [pageCriteria, appliedHeaderFilters, appliedAdvancedCriteria, chipCriteria]
  );
  // `enabled` must flip in the SAME render as the criteria it belongs to.
  // Applying filters sets hasInitiatedSearch immediately while the criteria were
  // still 400ms behind, which fired one throwaway request with stale criteria.
  const gate = useMemo(
    () => ({ criteria: combined, enabled: hasInitiatedSearch }),
    [combined, hasInitiatedSearch]
  );
  const debouncedGate = useDebounced(gate, 400);
  // Adjust-state-during-render (the documented React pattern): React re-renders
  // before committing, so the fetch effect only ever commits with page 1. As an
  // effect this ran AFTER the fetch had already gone out on the old page, costing
  // a second request. Covers every path that changes criteria — Apply, Reset,
  // chips, and parent-driven scope/stateVals changes alike.
  const prevCriteriaRef = useRef(debouncedGate.criteria);
  if (prevCriteriaRef.current !== debouncedGate.criteria) {
    prevCriteriaRef.current = debouncedGate.criteria;
    setCurrentPage(1);
  }
  const { records, setRecords, totalRows, totalPages, isLoading, refetch } =
    usePolicyTransactionList(StageType.QUALITYREVIEW, pageSize, currentPage, sortColumn, debouncedGate.criteria, debouncedGate.enabled);
  const handleRefresh = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  const applyEnabled = useMemo(() => {
    const hasAnyHeaderValue = Object.values(pendingHeaderFilters).some((v) => {
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });
    const hasAnyAdvancedValue = Object.keys(pendingAdvancedCriteria).length > 0;
    return hasAnyHeaderValue || hasAnyAdvancedValue;
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };
  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };
  const handleFilterChange = useCallback(
    (name: string, value: string | string[] | number | null) => {
      setPendingHeaderFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setPendingAdvancedCriteria({});
    setAppliedAdvancedCriteria({});
    setChipCriteria({});
    setHasInitiatedSearch(true);
  }, []);
  const handleChipCriteriaChange = useCallback(
    (next: Record<string, unknown>) => {
      setChipCriteria(next);
      // Skip the empty no-op emission that fires on tab mount (chip useEffect
      // runs on initial mount with default chips → derives `{}`). Once we've
      // started searching, any subsequent emission keeps the search alive.
      setHasInitiatedSearch((prev) => prev || Object.keys(next).length > 0);
    },
    []
  );
  const handleAdvancedCriteriaChange = useCallback(
    (next: Record<string, unknown>) => setPendingAdvancedCriteria(next),
    []
  );
  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setAppliedAdvancedCriteria(pendingAdvancedCriteria);
    setHasInitiatedSearch(true);
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  return (
    <QualityReviewTab
      records={records}
      allRecords={records}
      onUpdateRecords={setRecords}
      counts={counts}
      stateVals={stateVals}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPage={pageSize}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={totalRows}
      totalPages={totalPages}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      filters={pendingHeaderFilters}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      onChipCriteriaChange={handleChipCriteriaChange}
      onAdvancedCriteriaChange={handleAdvancedCriteriaChange}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      onRefresh={handleRefresh}
      userTimeZone={userTimeZone}
      headerFilterDefaults={headerFilterDefaults}
    />
  );
};

const PreCloseTabLoader = ({ scope, stateVals, counts, refetchCounts, userTimeZone, globalSeed }: StageLoaderProps) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("receivedDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [pendingAdvancedCriteria, setPendingAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [appliedAdvancedCriteria, setAppliedAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [chipCriteria, setChipCriteria] = useState<Record<string, unknown>>({});
  // Seeded rather than set from a mount effect: an effect would flip `enabled`
  // one render after the criteria were already debounced, costing an extra fetch.
  const [hasInitiatedSearch, setHasInitiatedSearch] =
    useState<boolean>(ALLOW_INITIAL_LOAD_PCQ || !!globalSeed);
  const pageCriteria = useMemo(
    () => ({ ...buildPageCriteria(scope, stateVals), ...globalSeed?.criteria }),
    [scope, stateVals, globalSeed]
  );
  const headerFilterDefaults = useMemo(
    () => toHeaderFilterDefaults(globalSeed?.criteria),
    [globalSeed]
  );
  const combined = useMemo(
    () => ({
      ...pageCriteria,
      ...appliedHeaderFilters,
      ...appliedAdvancedCriteria,
      ...chipCriteria,
    }),
    [pageCriteria, appliedHeaderFilters, appliedAdvancedCriteria, chipCriteria]
  );
  // `enabled` must flip in the SAME render as the criteria it belongs to.
  // Applying filters sets hasInitiatedSearch immediately while the criteria were
  // still 400ms behind, which fired one throwaway request with stale criteria.
  const gate = useMemo(
    () => ({ criteria: combined, enabled: hasInitiatedSearch }),
    [combined, hasInitiatedSearch]
  );
  const debouncedGate = useDebounced(gate, 400);
  // Adjust-state-during-render (the documented React pattern): React re-renders
  // before committing, so the fetch effect only ever commits with page 1. As an
  // effect this ran AFTER the fetch had already gone out on the old page, costing
  // a second request. Covers every path that changes criteria — Apply, Reset,
  // chips, and parent-driven scope/stateVals changes alike.
  const prevCriteriaRef = useRef(debouncedGate.criteria);
  if (prevCriteriaRef.current !== debouncedGate.criteria) {
    prevCriteriaRef.current = debouncedGate.criteria;
    setCurrentPage(1);
  }
  const { records, setRecords, totalRows, totalPages, isLoading, refetch } =
    usePolicyTransactionList(StageType.PRECLOSE, pageSize, currentPage, sortColumn, debouncedGate.criteria, debouncedGate.enabled);
  const handleRefresh = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  const applyEnabled = useMemo(() => {
    const hasAnyHeaderValue = Object.values(pendingHeaderFilters).some((v) => {
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });
    const hasAnyAdvancedValue = Object.keys(pendingAdvancedCriteria).length > 0;
    return hasAnyHeaderValue || hasAnyAdvancedValue;
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };
  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };
  const handleFilterChange = useCallback(
    (name: string, value: string | string[] | number | null) => {
      setPendingHeaderFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setPendingAdvancedCriteria({});
    setAppliedAdvancedCriteria({});
    setChipCriteria({});
    setHasInitiatedSearch(true);
  }, []);
  const handleChipCriteriaChange = useCallback(
    (next: Record<string, unknown>) => {
      setChipCriteria(next);
      // Skip the empty no-op emission that fires on tab mount (chip useEffect
      // runs on initial mount with default chips → derives `{}`). Once we've
      // started searching, any subsequent emission keeps the search alive.
      setHasInitiatedSearch((prev) => prev || Object.keys(next).length > 0);
    },
    []
  );
  const handleAdvancedCriteriaChange = useCallback(
    (next: Record<string, unknown>) => setPendingAdvancedCriteria(next),
    []
  );
  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setAppliedAdvancedCriteria(pendingAdvancedCriteria);
    setHasInitiatedSearch(true);
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  return (
    <PreCloseTab
      records={records}
      allRecords={records}
      onUpdateRecords={setRecords}
      counts={counts}
      stateVals={stateVals}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPage={pageSize}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={totalRows}
      totalPages={totalPages}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      filters={pendingHeaderFilters}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      onChipCriteriaChange={handleChipCriteriaChange}
      onAdvancedCriteriaChange={handleAdvancedCriteriaChange}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      onRefresh={handleRefresh}
      userTimeZone={userTimeZone}
      headerFilterDefaults={headerFilterDefaults}
    />
  );
};

const CloseQueueTabLoader = ({ scope, stateVals, counts, refetchCounts, userTimeZone, globalSeed }: StageLoaderProps) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("receivedDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [pendingAdvancedCriteria, setPendingAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [appliedAdvancedCriteria, setAppliedAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [pendingRequiredCriteria, setPendingRequiredCriteria] = useState<Record<string, unknown>>({});
  const [appliedRequiredCriteria, setAppliedRequiredCriteria] = useState<Record<string, unknown>>({});
  const [chipCriteria, setChipCriteria] = useState<Record<string, unknown>>({});
  // Seeded rather than set from a mount effect: an effect would flip `enabled`
  // one render after the criteria were already debounced, costing an extra fetch.
  const [hasInitiatedSearch, setHasInitiatedSearch] =
    useState<boolean>(ALLOW_INITIAL_LOAD_CQ || !!globalSeed);
  const pageCriteria = useMemo(
    () => ({ ...buildPageCriteria(scope, stateVals), ...globalSeed?.criteria }),
    [scope, stateVals, globalSeed]
  );
  const headerFilterDefaults = useMemo(
    () => toHeaderFilterDefaults(globalSeed?.criteria),
    [globalSeed]
  );
  const combined = useMemo(
    () => ({
      ...pageCriteria,
      ...appliedHeaderFilters,
      ...appliedAdvancedCriteria,
      ...appliedRequiredCriteria,
      ...chipCriteria,
    }),
    [pageCriteria, appliedHeaderFilters, appliedAdvancedCriteria, appliedRequiredCriteria, chipCriteria]
  );
  // `enabled` must flip in the SAME render as the criteria it belongs to.
  // Applying filters sets hasInitiatedSearch immediately while the criteria were
  // still 400ms behind, which fired one throwaway request with stale criteria.
  const gate = useMemo(
    () => ({ criteria: combined, enabled: hasInitiatedSearch }),
    [combined, hasInitiatedSearch]
  );
  const debouncedGate = useDebounced(gate, 400);
  // Adjust-state-during-render (the documented React pattern): React re-renders
  // before committing, so the fetch effect only ever commits with page 1. As an
  // effect this ran AFTER the fetch had already gone out on the old page, costing
  // a second request. Covers every path that changes criteria — Apply, Reset,
  // chips, and parent-driven scope/stateVals changes alike.
  const prevCriteriaRef = useRef(debouncedGate.criteria);
  if (prevCriteriaRef.current !== debouncedGate.criteria) {
    prevCriteriaRef.current = debouncedGate.criteria;
    setCurrentPage(1);
  }
  const { records, setRecords, totalRows, totalPages, isLoading, refetch } =
    usePolicyTransactionList(StageType.CLOSE, pageSize, currentPage, sortColumn, debouncedGate.criteria, debouncedGate.enabled);
  const handleRefresh = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);
  const applyEnabled = useMemo(() => {
    const hasAnyHeaderValue = Object.values(pendingHeaderFilters).some((v) => {
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });
    const hasAnyAdvancedValue = Object.keys(pendingAdvancedCriteria).length > 0;
    return hasAnyHeaderValue || hasAnyAdvancedValue;
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };
  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };
  const handleFilterChange = useCallback(
    (name: string, value: string | string[] | number | null) => {
      setPendingHeaderFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setPendingAdvancedCriteria({});
    setAppliedAdvancedCriteria({});
    setPendingRequiredCriteria({});
    setAppliedRequiredCriteria({});
    setChipCriteria({});
    setHasInitiatedSearch(true);
  }, []);
  const handleChipCriteriaChange = useCallback(
    (next: Record<string, unknown>) => {
      setChipCriteria(next);
      // Skip the empty no-op emission that fires on tab mount (chip useEffect
      // runs on initial mount with default chips → derives `{}`). Once we've
      // started searching, any subsequent emission keeps the search alive.
      setHasInitiatedSearch((prev) => prev || Object.keys(next).length > 0);
    },
    []
  );
  const handleAdvancedCriteriaChange = useCallback(
    (next: Record<string, unknown>) => setPendingAdvancedCriteria(next),
    []
  );
  const handleRequiredCriteriaChange = useCallback(
    (next: Record<string, unknown>) => setPendingRequiredCriteria(next),
    []
  );
  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setAppliedAdvancedCriteria(pendingAdvancedCriteria);
    setAppliedRequiredCriteria(pendingRequiredCriteria);
    setHasInitiatedSearch(true);
  }, [pendingHeaderFilters, pendingAdvancedCriteria, pendingRequiredCriteria]);
  return (
    <CloseQueueTab
      records={records}
      allRecords={records}
      onUpdateRecords={setRecords}
      counts={counts}
      stateVals={stateVals}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPage={pageSize}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={totalRows}
      totalPages={totalPages}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      filters={pendingHeaderFilters}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      onChipCriteriaChange={handleChipCriteriaChange}
      onAdvancedCriteriaChange={handleAdvancedCriteriaChange}
      onRequiredCriteriaChange={handleRequiredCriteriaChange}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      onRefresh={handleRefresh}
      userTimeZone={userTimeZone}
      headerFilterDefaults={headerFilterDefaults}
    />
  );
};

// History also pushes the page-level scope / state chips through as search
// criteria so server-side totals/paging match the visible rows.
const HistoryTabLoader = ({ scope, stateVals, counts, refetchCounts, userTimeZone, globalSeed }: StageLoaderProps) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("receivedDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [pendingAdvancedCriteria, setPendingAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [appliedAdvancedCriteria, setAppliedAdvancedCriteria] = useState<Record<string, unknown>>({});
  const [chipCriteria, setChipCriteria] = useState<Record<string, unknown>>({});
  // Seeded rather than set from a mount effect: an effect would flip `enabled`
  // one render after the criteria were already debounced, costing an extra fetch.
  const [hasInitiatedSearch, setHasInitiatedSearch] =
    useState<boolean>(ALLOW_INITIAL_LOAD_HISTORY || !!globalSeed);
  const pageCriteria = useMemo(
    () => ({ ...buildPageCriteria(scope, stateVals), ...globalSeed?.criteria }),
    [scope, stateVals, globalSeed]
  );
  const headerFilterDefaults = useMemo(
    () => toHeaderFilterDefaults(globalSeed?.criteria),
    [globalSeed]
  );
  const combined = useMemo(() => {
    const merged: Record<string, unknown> = {
      ...pageCriteria,
      ...appliedAdvancedCriteria,
      ...chipCriteria,
    };
    // Column-header inputs override Advanced Search on key collision (e.g.
    // RecordTypeCodeList / ProductTypeAbbrTypeCodeList), but only when the
    // header actually has a value — a cleared header lets the Advanced
    // selection apply.
    for (const [k, v] of Object.entries(appliedHeaderFilters)) {
      const empty =
        v == null ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "string" && v.trim() === "");
      if (!empty) merged[k] = v;
    }
    return merged;
  }, [pageCriteria, appliedAdvancedCriteria, appliedHeaderFilters, chipCriteria]);
  // `enabled` must flip in the SAME render as the criteria it belongs to.
  // Applying filters sets hasInitiatedSearch immediately while the criteria were
  // still 400ms behind, which fired one throwaway request with stale criteria.
  const gate = useMemo(
    () => ({ criteria: combined, enabled: hasInitiatedSearch }),
    [combined, hasInitiatedSearch]
  );
  const debouncedGate = useDebounced(gate, 400);
  // Adjust-state-during-render (the documented React pattern): React re-renders
  // before committing, so the fetch effect only ever commits with page 1. As an
  // effect this ran AFTER the fetch had already gone out on the old page, costing
  // a second request. Covers every path that changes criteria — Apply, Reset,
  // chips, and parent-driven scope/stateVals changes alike.
  const prevCriteriaRef = useRef(debouncedGate.criteria);
  if (prevCriteriaRef.current !== debouncedGate.criteria) {
    prevCriteriaRef.current = debouncedGate.criteria;
    setCurrentPage(1);
  }
  const { records, setRecords, totalRows, totalPages, isLoading, refetch } =
    usePolicyHistoryList(pageSize, currentPage, sortColumn, debouncedGate.criteria, debouncedGate.enabled);
  const applyEnabled = useMemo(() => {
    const hasAnyHeaderValue = Object.values(pendingHeaderFilters).some((v) => {
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });
    const hasAnyAdvancedValue = Object.keys(pendingAdvancedCriteria).length > 0;
    return hasAnyHeaderValue || hasAnyAdvancedValue;
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };
  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };
  const handleFilterChange = useCallback(
    (name: string, value: string | string[] | number | null) => {
      setPendingHeaderFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );
  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setPendingAdvancedCriteria({});
    setAppliedAdvancedCriteria({});
    setChipCriteria({});
    setHasInitiatedSearch(true);
  }, []);
  const handleChipCriteriaChange = useCallback(
    (next: Record<string, unknown>) => {
      setChipCriteria(next);
      // Skip the empty no-op emission that fires on tab mount (chip useEffect
      // runs on initial mount with default chips → derives `{}`). Once we've
      // started searching, any subsequent emission keeps the search alive.
      setHasInitiatedSearch((prev) => prev || Object.keys(next).length > 0);
    },
    []
  );
  const handleAdvancedCriteriaChange = useCallback(
    (next: Record<string, unknown>) => setPendingAdvancedCriteria(next),
    []
  );
  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setAppliedAdvancedCriteria(pendingAdvancedCriteria);
    setHasInitiatedSearch(true);
  }, [pendingHeaderFilters, pendingAdvancedCriteria]);
  return (
    <HistoryTab
      records={records}
      allRecords={records}
      onUpdateRecords={setRecords}
      counts={counts}
      stateVals={stateVals}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPage={pageSize}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={totalRows}
      totalPages={totalPages}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      filters={pendingHeaderFilters}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      onChipCriteriaChange={handleChipCriteriaChange}
      onAdvancedCriteriaChange={handleAdvancedCriteriaChange}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      onRefresh={refetch}
      refetchCounts={refetchCounts}
      userTimeZone={userTimeZone}
      headerFilterDefaults={headerFilterDefaults}
    />
  );
};

// Deleted Records queue
const DeletedRecordsTabLoader = ({ stateVals, userTimeZone }: StageLoaderProps) => {
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortColumn, setSortColumn] = useState<string>("receivedDate");
  const [pendingHeaderFilters, setPendingHeaderFilters] = useState<HeaderFilters>({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  console.log(appliedHeaderFilters);

  const [hasInitiatedSearch, setHasInitiatedSearch] = useState<boolean>(false);
  const [records, setRecords] = useState<PolicyManagementRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const applyEnabled = useMemo(
    () =>
      Object.values(pendingHeaderFilters).some((v) => {
        if (v == null) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "string") return v.trim() !== "";
        return true;
      }),
    [pendingHeaderFilters]
  );

  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1);
  };

  const handleSortChange = (next: string) => {
    setSortColumn(next);
    setCurrentPage(1);
  };

  const handleFilterChange = useCallback(
    (name: string, value: string | string[] | number | null) => {
      setPendingHeaderFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setPendingHeaderFilters({});
    setAppliedHeaderFilters({});
    setHasInitiatedSearch(false);
    setRecords([]);
    setCurrentPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedHeaderFilters(pendingHeaderFilters);
    setHasInitiatedSearch(true);
    setIsLoading(true);
    // Simulated network delay until the records are fetched from the backend.
    window.setTimeout(() => {
      setRecords(DUMMY_DELETED_RECORDS);
      setIsLoading(false);
    }, 300);
  }, [pendingHeaderFilters]);

  const refetch = useCallback(() => {
    if (!hasInitiatedSearch) return;
    setRecords(DUMMY_DELETED_RECORDS);
  }, [hasInitiatedSearch]);

  const handleRestore = useCallback(
    (selectedIds: string[]) => {
      setRecords((prev) => prev.filter((r) => !selectedIds.includes(r.uniqueIdentifier)));
      return Promise.resolve<RestorePolicyTransactionResultDto>({
        RestoredCount: selectedIds.length,
        FailedCount: 0,
      });
    },
    []
  );

  return (
    <DeletedRecordsTab
      records={records}
      stateVals={stateVals}
      onRowsPerPageChange={handlePageSizeChange}
      rowsPerPage={pageSize}
      page={currentPage}
      onPageChange={setCurrentPage}
      totalRows={records.length}
      totalPages={1}
      isLoading={isLoading}
      sortColumn={sortColumn}
      onSortChange={handleSortChange}
      filters={pendingHeaderFilters}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      applyEnabled={applyEnabled}
      onApplyFilters={handleApplyFilters}
      onRefresh={refetch}
      userTimeZone={userTimeZone}
      onRestore={handleRestore}
    />
  );
};

const PolicyManagementHome = ({
  tabSaveChangesRef,
}: PolicyManagementHomeProps) => {
  useTitle("Stewart Connect Policy Portal - Policy Management");

  // All four tabs load lazily inside their tab loaders so the API only fires
  // when a tab is active.
  const [scope, setScope] = useState<PageScope>("all");
  const [pageStates, setPageStates] = useState<OptionRenderType[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
    const [{ profileSettings }] = useProfileSettingsCache();
    const userTimeZone = profileSettings?.userTimeZone ?? "";

  const { stateList } = useStateList();

  const stateVals = useMemo(() => pageStates.map((s) => s.value), [pageStates]);

  const { counts, refetch: refetchCounts } = usePolicyManagementCounts({
    scope,
    pageStates: pageStates.map((s) => s.value),
  });

  const pageCriteria = useMemo(
    () => buildPageCriteria(scope, stateVals),
    [scope, stateVals]
  );

  // Global Search: opening a result row seeds that queue's loader with the
  // search criteria and switches to its tab. Seeding or clearing bumps the
  // queue's key, remounting the loader so its filter state starts fresh.
  const [globalSeeds, setGlobalSeeds] = useState<
    Partial<Record<GlobalSearchQueueKey, GlobalSearchSeed>>
  >({});
  const [loaderKeys, setLoaderKeys] = useState<Partial<Record<GlobalSearchQueueKey, number>>>({});
  const [tabSelect, setTabSelect] = useState<{ index: number; nonce: number } | null>(null);
  const nonceRef = useRef(0);

  const handleOpenQueue = useCallback(
    (row: GlobalSearchQueueRow, criteria: Record<string, string>, label: string) => {
      const nonce = ++nonceRef.current;
      setGlobalSeeds((prev) => ({ ...prev, [row.key]: { criteria, label, nonce } }));
      setLoaderKeys((prev) => ({ ...prev, [row.key]: nonce }));
      setTabSelect({ index: row.tabIndex, nonce });
    },
    []
  );

  const handleClearGlobalSeed = useCallback((key: GlobalSearchQueueKey) => {
    const nonce = ++nonceRef.current;
    setGlobalSeeds((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setLoaderKeys((prev) => ({ ...prev, [key]: nonce }));
  }, []);

  // Global Search "Clear": drop every seed and remount those queues so their
  // tab filters (header boxes, chips, advanced search) start fresh too.
  const handleClearAllGlobalSeeds = useCallback(() => {
    const keys = Object.keys(globalSeeds) as GlobalSearchQueueKey[];
    if (!keys.length) return;
    const nonce = ++nonceRef.current;
    setGlobalSeeds({});
    setLoaderKeys((prev) => ({ ...prev, ...Object.fromEntries(keys.map((k) => [k, nonce])) }));
  }, [globalSeeds]);

  // Wraps a queue loader with its Global Search banner and remount key.
  const renderQueue = (key: GlobalSearchQueueKey, loader: React.ReactNode) => {
    const seed = globalSeeds[key];
    return (
      <React.Fragment key={loaderKeys[key] ?? 0}>
        {seed && (
          <GlobalSearchFilterBanner
            label={seed.label}
            onClear={() => handleClearGlobalSeed(key)}
          />
        )}
        {loader}
      </React.Fragment>
    );
  };

  const tabs: TabNavigatorItem[] = [
    { title: `Quality Review (${counts.qrTotal})`, disabled: false },
    { title: `Pre-Close (${counts.pcqTotal})`, disabled: false },
    { title: `Close Queue (${counts.cqTotal})`, disabled: false },
    { title: "History", disabled: false },
    { title: "Deleted Records", disabled: false } // hide this when the user doesn't have this permission? 
  ];

  return (
    <Container>
      <Box className="headerContainer">
        <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1, flexWrap: "wrap" }}>
          <Box className="header">
            <GradientIcon Icon={PolicyManagementIcon} />
            <Typography
              className="titleQuickReferenceCard"
              variant="h6"
            >
              Policy Management
            </Typography>
          </Box>

          <ScopeChip
            className={scope === "all" ? "active" : ""}
            onClick={() => setScope("all")}
          >
            All
          </ScopeChip>
          <ScopeChip
            className={scope === "national" ? "active" : ""}
            onClick={() => setScope("national")}
          >
            National
          </ScopeChip>
          <ScopeChip
            className={scope === "nonNational" ? "active" : ""}
            onClick={() => setScope("nonNational")}
          >
            Non-National
          </ScopeChip>

          <Box
            sx={{
              minWidth: 200,
              maxWidth: 260,
              "& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiInputBase-input": {
                backgroundColor: colors.white,
              },
              // Shared dropdown forces 40px input height; MUI's default un-shrunk
              // label transform is calibrated for a 56px input and sits too low.
              "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
                transform: "translate(14px, 10px) scale(1)",
              },
            }}
          >
            <StewartAutocompleteWithCheckboxes
              name="pageStates"
              placeholder="State"
              options={stateList}
              defaultSelectedOption={pageStates}
              onChange={(_e, value) => setPageStates(value)}
              enableClearable
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1, flexWrap: "wrap" }}>
          <GlobalSearchBar
            pageCriteria={pageCriteria}
            onOpenQueue={handleOpenQueue}
            onResetQueueFilters={handleClearAllGlobalSeeds}
          />

          <ActionButton
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Registers
          </ActionButton>
        </Box>
      </Box>

      <TabNavigator
        tabs={tabs}
        tabSaveChangesRef={tabSaveChangesRef}
        selectRequest={tabSelect}
      >
        {/* Quality Review Tab — loader fires API only when this tab is active */}
        {renderQueue("qr", <QualityReviewTabLoader scope={scope} stateVals={stateVals} counts={counts} refetchCounts={refetchCounts} userTimeZone={userTimeZone} globalSeed={globalSeeds.qr} />)}

        {/* Pre-Close Tab — loader fires API only when this tab is active */}
        {renderQueue("preclose", <PreCloseTabLoader scope={scope} stateVals={stateVals} counts={counts} refetchCounts={refetchCounts} userTimeZone={userTimeZone} globalSeed={globalSeeds.preclose} />)}

        {/* Close Queue Tab — loader fires API only when this tab is active */}
        {renderQueue("close", <CloseQueueTabLoader scope={scope} stateVals={stateVals} counts={counts} refetchCounts={refetchCounts} userTimeZone={userTimeZone} globalSeed={globalSeeds.close} />)}

        {/* History Tab — loader fires API only when this tab is active */}
        {renderQueue("history", <HistoryTabLoader scope={scope} stateVals={stateVals} counts={counts} refetchCounts={refetchCounts} userTimeZone={userTimeZone} globalSeed={globalSeeds.history} />)}

        <DeletedRecordsTabLoader scope={scope} stateVals={stateVals} counts={counts} refetchCounts={refetchCounts} userTimeZone={userTimeZone} />
      </TabNavigator>

      <UploadRegistersModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
    </Container>
  );
};

export default PolicyManagementHome;
